import { Component, type ReactNode, type ErrorInfo } from 'react';
import { AlertTriangle, Download, RotateCcw } from 'lucide-react';

interface Props { children: ReactNode }
interface State { error: Error | null; info: ErrorInfo | null }

// A new dashboard build replaces the chunk-hash filenames every deploy. If a
// user has the old `index.js` loaded when a deploy lands, any code-split
// import (e.g. RuleEditor) will 404 because the hash changed. We surface a
// focused "reload to update" panel for that class of failure instead of the
// generic crash UI — message wording covers Chromium / Firefox / Safari.
function isChunkLoadError(err: Error): boolean {
  const msg = err.message || '';
  return /Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(msg);
}

function reloadFresh() {
  // Cache-bust query so any intermediary (Railway edge, browser, service
  // worker) hands us the new index.html on this exact navigation.
  const url = new URL(window.location.href);
  url.searchParams.set('cb', Date.now().toString());
  window.location.replace(url.toString());
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[boundary] caught', error, info);
    this.setState({ info });
  }

  render() {
    if (!this.state.error) return this.props.children;

    if (isChunkLoadError(this.state.error)) {
      return (
        <div className="h-dvh grid place-items-center bg-[var(--bg-base)] text-[var(--fg-primary)] p-6">
          <div className="max-w-md w-full rounded-lg border border-[var(--accent-brand)]/40 bg-[var(--bg-surface)] p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Download className="w-5 h-5 text-[var(--accent-brand)]" />
              <h1 className="font-display font-semibold text-base">Update available</h1>
            </div>
            <p className="text-xs text-[var(--fg-muted)]">
              A new dashboard build was deployed. Reload to pick it up — your local state will be preserved.
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={reloadFresh}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[var(--accent-brand)] hover:bg-[var(--accent-brand-hover)] text-white text-xs font-medium transition-colors"
              >
                <RotateCcw className="w-3 h-3" />
                reload now
              </button>
              <button
                onClick={() => { sessionStorage.clear(); localStorage.clear(); window.location.replace('/'); }}
                className="ml-auto text-[10px] text-[var(--fg-faint)] hover:text-[var(--accent-destructive)]"
              >
                clear session
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="h-dvh grid place-items-center bg-[var(--bg-base)] text-[var(--fg-primary)] p-6">
        <div className="max-w-xl w-full rounded-lg border border-[var(--accent-destructive)]/40 bg-[var(--bg-surface)] p-5 space-y-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[var(--accent-destructive)]" />
            <h1 className="font-display font-semibold text-base">Something broke</h1>
          </div>
          <p className="text-xs text-[var(--fg-muted)]">
            The dashboard encountered an unexpected error. The app stayed mounted so you can recover without losing
            local state. Refresh, or clear your session and sign in again if the problem persists.
          </p>
          <pre className="text-[11px] font-display tabular bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded p-2 overflow-auto max-h-48 text-[var(--fg-muted)]">
            {this.state.error.message}
            {this.state.info?.componentStack ? `\n${this.state.info.componentStack}` : ''}
          </pre>
          <div className="flex items-center gap-2">
            <button
              onClick={() => this.setState({ error: null, info: null })}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[var(--accent-brand)] hover:bg-[var(--accent-brand-hover)] text-white text-xs font-medium transition-colors"
            >
              <RotateCcw className="w-3 h-3" />
              try again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-3 py-1.5 rounded-md border border-[var(--border-strong)] bg-[var(--bg-base)] text-[var(--fg-muted)] hover:text-[var(--fg-primary)] text-xs"
            >
              hard reload
            </button>
            <button
              onClick={() => { sessionStorage.clear(); localStorage.clear(); window.location.replace('/'); }}
              className="ml-auto text-[10px] text-[var(--fg-faint)] hover:text-[var(--accent-destructive)]"
            >
              clear session
            </button>
          </div>
        </div>
      </div>
    );
  }
}
